// SETTINGS › EMAIL SIGNATURE (v3 — Karan's brief, 31/10/2026, PROMPT.md §4a).
//
// One panel for the words under every email the practice sends:
//   • the company's signature details (organisations/{org}.signature) — edited
//     by the Owner only (the rules say so too); a Director sees them read-only;
//   • a live preview, rendered by public/signature-render.js — the SAME file the
//     Cloud Function pushes with — in a sandboxed <iframe srcdoc>, never in the
//     app's DOM;
//   • the four image states (Images on · Outlook blocked · Gmail blocked · Apple
//     Mail blocked) and a dark-mode check. Those are a PREVIEW AID: the pushed
//     HTML is always the real template;
//   • "Send test to me": the caller's own Gmail signature, plus a one-line email
//     to the caller, and only the caller (Karan 31/10: "the email update should
//     just be to the signature creator");
//   • "Push to everyone": a dry run first, with the per-person hash table, then
//     a push of only the signatures that changed. It emails nobody.
//
// Owner and Director. Data comes in through `deps` so the screenshot harness can
// draw it over sample data; in the app it is db.js.
import { escapeHtml as esc } from '../ui.js';
import { renderSignatureParts, previewDocument, IMAGE_STATES, validateOrgSignature } from '../signature-render.js';

// [key, label, options] — in the order they appear in the signature.
const FIELDS = [
  ['logoUrl', 'Logo image', { mono: true, wide: true, sub: 'https · a PNG on white, so it survives dark mode' }],
  ['logoWidth', 'Logo width', { mono: true, sub: 'px' }],
  ['logoHeight', 'Logo height', { mono: true, sub: 'px' }],
  ['logoAlt', 'Logo alt text', { wide: true, sub: 'keep it short: Outlook draws it inside the logo’s box' }],
  ['descriptorLine1', 'Under the logo, line 1', {}],
  ['descriptorLine2', 'Under the logo, line 2', {}],
  ['officeTel', 'Office number', { mono: true, sub: 'with the country code; a person can set their own' }],
  ['websiteUrl', 'Website', { mono: true }],
  ['websiteLabel', 'Website shown as', {}],
  ['registeredName', 'Registered name', {}],
  ['placeOfRegistration', 'Registered in', {}],
  ['companyNumber', 'Company number', { mono: true, sub: '8 digits' }],
  ['registeredOffice', 'Registered office', { wide: true }],
  ['legalUrl', 'Legal page', { mono: true, sub: 'https' }],
  ['legalLabel', 'Legal link text', {}],
  ['gmailDomain', 'Mail domain', { mono: true, sub: 'only these mailboxes are written to' }],
];

const RESULT_TEXT = {
  'would-push': 'Will change', unchanged: 'Up to date', skipped: 'Skipped', error: 'Will not render',
  pushed: 'Pushed', 'dry-run': 'Previewed',
};

async function defaultDeps() {
  const m = await import('../db.js');
  return {
    loadConfig: m.orgSignatureConfig, saveConfig: m.saveOrgSignature, loadUsers: m.allUsers,
    push: m.pushSignatures, emailTest: m.emailSignatureTest,
  };
}

const short = (h) => (h ? String(h).slice(0, 10) : '—');
const nameOf = (u) => String(u.displayName || u.email || u.id || '').trim();

/**
 * @param mount    the Settings main column
 * @param session  { email, roles }
 * @param deps     optional { loadConfig, saveConfig, loadUsers, push, emailTest }
 */
export async function renderSignatureSettings(mount, session, deps = null) {
  const roles = session.roles || [];
  const isOwner = roles.includes('Owner');
  const canAct = isOwner || roles.includes('Director');
  if (!canAct) { mount.innerHTML = '<p class="st-help">The email signature is for the Owner and Directors.</p>'; return; }
  mount.innerHTML = '<h2 class="st-h2">Email signature</h2><div class="skel" style="height:260px"></div>';

  const d = deps || await defaultDeps();
  let cfg; let users;
  try {
    [cfg, users] = await Promise.all([d.loadConfig().then((c) => c || {}), d.loadUsers()]);
  } catch (e) {
    mount.innerHTML = `<h2 class="st-h2">Email signature</h2><p class="error">Couldn’t load the signature settings — ${esc(e.message || String(e))}</p>`;
    return;
  }
  users = (users || []).filter((u) => u.active !== false && !u.noSignIn)
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
  const meEmail = String(session.email || '').toLowerCase();
  const me = users.find((u) => String(u.email || '').toLowerCase() === meEmail);

  const draft = {};
  for (const [k] of FIELDS) draft[k] = cfg[k] == null ? '' : String(cfg[k]);
  draft.skipEmails = (Array.isArray(cfg.skipEmails) ? cfg.skipEmails : []).join('\n');
  let sampleId = (me && (me.id || me.uid)) || (users[0] && (users[0].id || users[0].uid)) || '';
  let imgState = 'on';
  let dark = false;
  let errors = {};
  let dry = null;            // { results, counts } from the last dry run
  let pushed = null;         // { results, counts } from the last real push
  let busy = false;
  let msg = { text: '', bad: false };

  const sample = () => users.find((u) => (u.id || u.uid) === sampleId) || me || {};
  const liveOrg = () => ({ ...cfg, ...draft, skipEmails: undefined });

  const frameDoc = () => {
    try {
      const { pretty } = renderSignatureParts(liveOrg(), sample());
      return previewDocument(pretty, { state: imgState, dark });
    } catch (e) {
      return `<!doctype html><body style="font-family:Arial,sans-serif;font-size:13px;padding:20px">${esc(e.message || String(e))}</body>`;
    }
  };
  const paintFrame = () => {
    const f = mount.querySelector('[data-sg-frame]');
    if (f) f.srcdoc = frameDoc();
  };

  const missing = () => {
    const v = validateOrgSignature({ ...cfg });
    return Object.keys(v.errors);
  };

  const field = ([k, label, o]) => {
    const id = `sg3-${k}`;
    const err = errors[k];
    return `<div class="st-field${o.wide ? ' sg3-wide' : ''}${err ? ' bad' : ''}">
      <label class="st-label" for="${id}">${esc(label)}${o.sub ? ` <span class="st-label-sub">${esc(o.sub)}</span>` : ''}</label>
      <input class="st-input${o.mono ? ' mono' : ''}" id="${id}" data-sg-k="${k}" value="${esc(draft[k])}" autocomplete="off"${isOwner ? '' : ' disabled'}>
      ${err ? `<span class="st-err">${esc(err)}</span>` : ''}</div>`;
  };

  const resultRows = (res) => res.results.map((r) => {
    const notes = [
      r.reason ? r.reason : '',
      r.error ? r.error : '',
      (r.missing || []).includes('mobile') ? 'no mobile' : '',
      ...(r.warnings || []),
    ].filter(Boolean).join(' · ');
    const tone = r.result === 'error' ? ' bad' : r.result === 'would-push' || r.result === 'pushed' ? ' on' : '';
    return `<tr>
      <td>${esc(r.name || r.uid)}</td>
      <td class="mono">${esc(r.email || '—')}</td>
      <td><span class="sg3-res${tone}">${esc(RESULT_TEXT[r.result] || r.result)}</span></td>
      <td class="mono">${r.hash ? `${esc(short(r.previousHash))} → ${esc(short(r.hash))}` : '—'}</td>
      <td class="sg3-notes">${esc(notes)}</td></tr>`;
  }).join('');

  const table = (res, title) => `
    <div class="sg3-tablewrap">
      <span class="st-caps">${esc(title)}</span>
      <table class="sg3-table">
        <thead><tr><th>Person</th><th>Mailbox</th><th>Result</th><th>Hash (last pushed → now)</th><th>Notes</th></tr></thead>
        <tbody>${resultRows(res)}</tbody>
      </table>
    </div>`;

  const draw = () => {
    const toPush = dry ? dry.results.filter((r) => r.result === 'would-push') : [];
    const gaps = missing();
    mount.innerHTML = `
      <h2 class="st-h2">Email signature</h2>
      <p class="st-help">What every email from the practice says underneath it. Track writes it into each person’s Gmail; nothing reaches Gmail until somebody pushes it here${cfg.autoPush === true ? ' or changes a record (automatic pushes are on)' : ''}.</p>
      ${gaps.length ? `<p class="sg3-warn">Not ready to push: ${esc(gaps.join(', '))} ${gaps.length === 1 ? 'is' : 'are'} missing or wrong below. Pushing refuses until ${gaps.length === 1 ? 'it is' : 'they are'} set.</p>` : ''}

      <div class="st-panel">
        <div class="sg3-bar">
          <label class="st-label sg3-who">Preview as
            <select class="st-input" data-sg-sample>${users.map((u) => `<option value="${esc(u.id || u.uid)}"${(u.id || u.uid) === sampleId ? ' selected' : ''}>${esc(nameOf(u))}</option>`).join('')}</select>
          </label>
          <div class="st-chips sg3-states" role="group" aria-label="Image state">
            ${IMAGE_STATES.map((s) => `<button type="button" class="st-chip${s.key === imgState ? ' on' : ''}" aria-pressed="${s.key === imgState}" data-sg-state="${s.key}">${esc(s.label)}</button>`).join('')}
          </div>
          <span class="sg3-dark"><span class="st-label">Dark mode</span><button class="st-sw${dark ? ' on' : ''}" type="button" role="switch" aria-checked="${dark}" aria-label="Dark mode" data-sg-dark><span class="st-knob"></span></button></span>
        </div>
        <iframe class="sg3-frame" data-sg-frame sandbox="" title="Signature preview" referrerpolicy="no-referrer"></iframe>
        <p class="st-field-note">The blocked and dark views show what each mail client draws. Gmail is always sent the real signature.</p>
        <div class="sg3-actions">
          <button class="btn" type="button" data-sg-test${busy ? ' disabled' : ''}>Send test to me</button>
          <button class="btn primary" type="button" data-sg-dry${busy ? ' disabled' : ''}>Push to everyone</button>
          <span class="sg3-msg${msg.bad ? ' bad' : ''}" role="status">${esc(msg.text)}</span>
        </div>
        <p class="st-field-note">Send test to me updates your own Gmail signature and emails you, and only you. Push to everyone emails nobody: it shows who would change first, then you push.</p>
        ${dry ? `${table(dry, 'Dry run — nothing written yet')}
          <div class="sg3-actions">
            ${toPush.length ? `<button class="btn primary" type="button" data-sg-push${busy ? ' disabled' : ''}>Push ${toPush.length} ${toPush.length === 1 ? 'signature' : 'signatures'} to Gmail</button>` : '<span class="st-field-note">Nothing to push: everyone is up to date or skipped.</span>'}
            <button class="btn ghost" type="button" data-sg-cancel>Close</button>
          </div>` : ''}
        ${pushed ? table(pushed, 'Pushed') : ''}
      </div>

      <div class="st-panel">
        <span class="st-caps">Company details in the signature</span>
        ${isOwner ? '' : '<p class="st-field-note">Only the Owner can change these.</p>'}
        <div class="st-grid two sg3-fields">${FIELDS.map(field).join('')}</div>
        <div class="st-field">
          <label class="st-label" for="sg3-skip">Never touch these mailboxes <span class="st-label-sub">one per line · shared mailboxes keep their own signature</span></label>
          <textarea class="st-input mono sg3-skip" id="sg3-skip" data-sg-k="skipEmails" rows="3"${isOwner ? '' : ' disabled'}>${esc(draft.skipEmails)}</textarea>
        </div>
        ${isOwner ? `<div class="sg3-actions"><button class="btn primary" type="button" data-sg-save${busy ? ' disabled' : ''}>Save details</button>
          <span class="st-field-note">Saving changes the preview for everyone. It does not push.</span></div>` : ''}
      </div>`;
    paintFrame();
    wire();
  };

  const say = (text, bad = false) => {
    msg = { text, bad };
    const n = mount.querySelector('.sg3-msg');
    if (n) { n.textContent = text; n.classList.toggle('bad', bad); }
  };
  const errText = (e) => (e && e.message) || String(e);

  async function save() {
    const v = validateOrgSignature({ ...draft });
    errors = v.errors;
    if (!v.ok) { draw(); say('Check the highlighted fields.', true); return; }
    const next = {};
    for (const [k] of FIELDS) {
      const val = v.value[k];
      if (String(cfg[k] == null ? '' : cfg[k]) !== String(val)) next[k] = val;
    }
    if (String(cfg.officeTelE164 || '') !== v.value.officeTelE164) next.officeTelE164 = v.value.officeTelE164;
    const skip = String(draft.skipEmails || '').split(/[\n,]/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (JSON.stringify(skip) !== JSON.stringify(cfg.skipEmails || [])) next.skipEmails = skip;
    if (!Object.keys(next).length) { say('Nothing changed.'); return; }
    busy = true; draw();
    try {
      await d.saveConfig(next);
      cfg = { ...cfg, ...next };
      dry = null; pushed = null;
      busy = false; draw();
      say('Saved. Nothing has been pushed.');
    } catch (e) { busy = false; draw(); say(errText(e), true); }
  }

  async function sendTest() {
    busy = true; draw(); say('Updating your own signature…');
    try {
      const res = await d.push({ dryRun: false, self: true });
      const mine = (res.results || [])[0] || {};
      if (mine.result === 'skipped' || mine.result === 'error') {
        busy = false; draw(); say(`Not sent: ${mine.reason || mine.error}.`, true); return;
      }
      const mail = await d.emailTest();
      busy = false; draw();
      const sig = mine.result === 'pushed' ? 'Your Gmail signature is updated.' : 'Your Gmail signature was already current.';
      say(mail.dryRun
        ? `${sig} The test email to ${mail.to} was not sent: test emails are in dry run until SIGNATURE_MAIL_DRY_RUN is switched off.`
        : `${sig} Test email sent to ${mail.to}.`);
    } catch (e) { busy = false; draw(); say(errText(e), true); }
  }

  async function dryRun() {
    busy = true; pushed = null; draw(); say('Rendering everyone’s signature (a dry run)…');
    try {
      dry = await d.push({ dryRun: true });
      busy = false; draw();
      const c = dry.counts || {};
      say(Object.entries(c).map(([k, n]) => `${n} ${(RESULT_TEXT[k] || k).toLowerCase()}`).join(', ') || 'Nobody to push.');
    } catch (e) { busy = false; dry = null; draw(); say(errText(e), true); }
  }

  async function pushChanged() {
    const uids = dry.results.filter((r) => r.result === 'would-push').map((r) => r.uid);
    if (!uids.length) return;
    busy = true; draw(); say(`Pushing ${uids.length}…`);
    try {
      pushed = await d.push({ dryRun: false, uids });
      dry = null; busy = false; draw();
      const c = pushed.counts || {};
      say(Object.entries(c).map(([k, n]) => `${n} ${(RESULT_TEXT[k] || k).toLowerCase()}`).join(', '));
    } catch (e) { busy = false; draw(); say(errText(e), true); }
  }

  function wire() {
    mount.querySelector('[data-sg-sample]')?.addEventListener('change', (e) => { sampleId = e.target.value; paintFrame(); });
    mount.querySelectorAll('[data-sg-state]').forEach((b) => b.addEventListener('click', () => {
      imgState = b.dataset.sgState;
      mount.querySelectorAll('[data-sg-state]').forEach((x) => { const on = x === b; x.classList.toggle('on', on); x.setAttribute('aria-pressed', String(on)); });
      paintFrame();
    }));
    mount.querySelector('[data-sg-dark]')?.addEventListener('click', (e) => {
      dark = !dark;
      e.currentTarget.classList.toggle('on', dark);
      e.currentTarget.setAttribute('aria-checked', String(dark));
      paintFrame();
    });
    // Typing re-renders the preview only, so the field keeps its focus.
    mount.querySelectorAll('[data-sg-k]').forEach((el) => el.addEventListener('input', () => { draft[el.dataset.sgK] = el.value; paintFrame(); }));
    mount.querySelector('[data-sg-save]')?.addEventListener('click', save);
    mount.querySelector('[data-sg-test]')?.addEventListener('click', sendTest);
    mount.querySelector('[data-sg-dry]')?.addEventListener('click', dryRun);
    mount.querySelector('[data-sg-push]')?.addEventListener('click', pushChanged);
    mount.querySelector('[data-sg-cancel]')?.addEventListener('click', () => { dry = null; draw(); say(''); });
  }

  draw();
}

/**
 * The read-only card on a person's profile (§4a): their signature as it renders
 * now, and when it last reached Gmail. Same renderer, same sandboxed frame.
 * @param el    a host element
 * @param opts  { cfg, user, onEditMine? } — onEditMine is offered on your own record only
 */
export function mountSignatureCard(el, { cfg, user, onEditMine = null }) {
  if (!el || !cfg) return;
  let pretty = '';
  try { pretty = renderSignatureParts(cfg, user).pretty; } catch (_) { pretty = ''; }
  const sig = user.signature || {};
  const at = sig.lastPushedAt && typeof sig.lastPushedAt.toDate === 'function' ? sig.lastPushedAt.toDate() : (sig.lastPushedAt ? new Date(sig.lastPushedAt) : null);
  const when = at && Number.isFinite(at.getTime())
    ? `In Gmail since ${at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
    : 'Not pushed to Gmail yet';
  const off = user.signatureEnabled === false ? ' · turned off for this person' : '';
  el.innerHTML = `
    <div class="bm-eyebrow blue">Email signature</div>
    <iframe class="sg3-frame sg3-card" data-sg-card sandbox="" title="Email signature" referrerpolicy="no-referrer"></iframe>
    <div class="sg3-actions">
      <span class="st-field-note">${esc(when + off)}${sig.lastError ? ` · last push failed: ${esc(sig.lastError)}` : ''}</span>
      ${onEditMine ? '<button class="btn ghost" type="button" data-sg-editmine>Edit my mobile, office number and post-nominals</button>' : ''}
    </div>`;
  el.querySelector('[data-sg-card]').srcdoc = previewDocument(pretty || '<p style="font-family:Arial,sans-serif;font-size:13px">No signature yet.</p>');
  el.querySelector('[data-sg-editmine]')?.addEventListener('click', onEditMine);
}
